/**
 * Sync Conflict Resolution Dialog
 * Shows when there's a conflict between local and server data
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { AlertTriangle, Cloud, Smartphone, GitMerge, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface ConflictData {
  id: string;
  entityType: 'order' | 'order_item' | 'category' | 'menu_item';
  entityId: string;
  field: string;
  localValue: unknown;
  serverValue: unknown;
  localUpdatedAt: string;
  serverUpdatedAt: string;
  description?: string;
}

export type ConflictResolution = 'keep_local' | 'use_server' | 'merge';

interface SyncConflictDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conflicts: ConflictData[];
  onResolve: (conflictId: string, resolution: ConflictResolution) => void;
  onResolveAll: (resolution: ConflictResolution) => void;
}

export const SyncConflictDialog: React.FC<SyncConflictDialogProps> = ({
  open,
  onOpenChange,
  conflicts,
  onResolve,
  onResolveAll,
}) => {
  const { t } = useTranslation();
  const [selectedResolutions, setSelectedResolutions] = useState<Record<string, ConflictResolution>>({});

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const getEntityTypeLabel = (type: ConflictData['entityType']): string => {
    const labels: Record<ConflictData['entityType'], string> = {
      order: t('offline.conflict.entity.order', 'Order'),
      order_item: t('offline.conflict.entity.order_item', 'Order Item'),
      category: t('offline.conflict.entity.category', 'Category'),
      menu_item: t('offline.conflict.entity.menu_item', 'Menu Item'),
    };
    return labels[type];
  };

  const handleResolutionSelect = (conflictId: string, resolution: ConflictResolution) => {
    setSelectedResolutions(prev => ({
      ...prev,
      [conflictId]: resolution,
    }));
  };

  const handleApplySelected = () => {
    Object.entries(selectedResolutions).forEach(([conflictId, resolution]) => {
      onResolve(conflictId, resolution);
    });
    onOpenChange(false);
  };

  const canApply = Object.keys(selectedResolutions).length === conflicts.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            {t('offline.conflict.title', 'Sync Conflicts Detected')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'offline.conflict.description',
              'Some data was modified both locally and on the server. Choose how to resolve each conflict.'
            )}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[50vh] pr-4">
          <div className="space-y-4">
            {conflicts.map((conflict, index) => (
              <Card key={conflict.id} className="border-yellow-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">
                        {getEntityTypeLabel(conflict.entityType)}
                      </Badge>
                      <span className="text-muted-foreground">
                        {conflict.field}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      #{index + 1} of {conflicts.length}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {conflict.description && (
                    <p className="text-sm text-muted-foreground">
                      {conflict.description}
                    </p>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    {/* Local Value */}
                    <div
                      className={cn(
                        'p-3 rounded-lg border-2 cursor-pointer transition-all',
                        selectedResolutions[conflict.id] === 'keep_local'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      )}
                      onClick={() => handleResolutionSelect(conflict.id, 'keep_local')}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Smartphone className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium">
                          {t('offline.conflict.local', 'Local (Your Device)')}
                        </span>
                      </div>
                      <pre className="text-xs bg-white p-2 rounded overflow-auto max-h-20">
                        {formatValue(conflict.localValue)}
                      </pre>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(conflict.localUpdatedAt)}
                      </p>
                    </div>

                    {/* Server Value */}
                    <div
                      className={cn(
                        'p-3 rounded-lg border-2 cursor-pointer transition-all',
                        selectedResolutions[conflict.id] === 'use_server'
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-green-300'
                      )}
                      onClick={() => handleResolutionSelect(conflict.id, 'use_server')}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Cloud className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium">
                          {t('offline.conflict.server', 'Server (Latest)')}
                        </span>
                      </div>
                      <pre className="text-xs bg-white p-2 rounded overflow-auto max-h-20">
                        {formatValue(conflict.serverValue)}
                      </pre>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(conflict.serverUpdatedAt)}
                      </p>
                    </div>
                  </div>

                  {/* Resolution Options */}
                  <div className="flex items-center justify-center gap-2 pt-2">
                    <Button
                      variant={selectedResolutions[conflict.id] === 'keep_local' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleResolutionSelect(conflict.id, 'keep_local')}
                      className="flex-1"
                    >
                      <Smartphone className="h-3 w-3 mr-1" />
                      {t('offline.conflict.keep_mine', 'Keep Mine')}
                    </Button>
                    <Button
                      variant={selectedResolutions[conflict.id] === 'use_server' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleResolutionSelect(conflict.id, 'use_server')}
                      className="flex-1"
                    >
                      <Cloud className="h-3 w-3 mr-1" />
                      {t('offline.conflict.use_server', 'Use Server')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </ScrollArea>

        <Separator />

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="flex gap-2 flex-1">
            <Button
              variant="outline"
              onClick={() => onResolveAll('keep_local')}
              className="flex-1"
            >
              <Smartphone className="h-4 w-4 mr-2" />
              {t('offline.conflict.keep_all_local', 'Keep All Local')}
            </Button>
            <Button
              variant="outline"
              onClick={() => onResolveAll('use_server')}
              className="flex-1"
            >
              <Cloud className="h-4 w-4 mr-2" />
              {t('offline.conflict.use_all_server', 'Use All Server')}
            </Button>
          </div>
          <Button
            onClick={handleApplySelected}
            disabled={!canApply}
          >
            <ArrowRight className="h-4 w-4 mr-2" />
            {t('offline.conflict.apply_selected', 'Apply Selected')}
            {canApply && ` (${conflicts.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SyncConflictDialog;
